(function () {
  "use strict";

  /**
   * Lazily loads the mobile drawer menu with the Section Rendering API.
   *
   * The full menu is no longer rendered in the page HTML for every visitor.
   * It is requested on the first interaction with the burger button and the
   * markup is injected into the existing drawer. The desktop menu remains the
   * single server-rendered copy of the navigation.
   */

  const SECTION_ID = "header-mobile-menu";

  const selectors = {
    drawer: "#headerDrawerMenu",
    nav: ".js-mobile-header-main-menu",
    placeholder: "[data-mobile-menu-placeholder]",
    burger: ".js-main-menu-toggler",
    closeButton: ".js-header-drawer-close-button",
    toggler: ".js-mobile-header-menu-toggler",
    panel: ".header-mobile__menu-list-wrapper",
  };

  const classes = {
    active: "is-active",
    hidden: "is-hidden",
    childActive: "is-child-active",
    grandChildActive: "is-grand-child-active",
  };

  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const drawer = document.querySelector(selectors.drawer);

  if (!drawer) {
    return;
  }

  const nav = drawer.querySelector(selectors.nav);

  if (!nav) {
    return;
  }

  const hideTimers = new WeakMap();
  const boundTogglers = new WeakSet();
  let loadState = "idle";
  let loadRequest = null;

  function getMenuUrl() {
    const currentUrl = new URL(window.location.href);
    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";
    const url = new URL(root, window.location.origin);

    url.searchParams.set("section_id", SECTION_ID);

    // Keep theme editor previews on the theme being previewed.
    const previewThemeId = currentUrl.searchParams.get("preview_theme_id");

    if (previewThemeId) {
      url.searchParams.set("preview_theme_id", previewThemeId);
    }

    return url.toString();
  }

  function loadMenu() {
    if (loadState === "loaded") {
      return Promise.resolve(true);
    }

    if (loadRequest) {
      return loadRequest;
    }

    loadState = "loading";

    loadRequest = fetch(getMenuUrl(), {
      credentials: "same-origin",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error("Mobile menu request failed with " + response.status);
        }

        return response.text();
      })
      .then(function (html) {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const fetchedNav = parsed.querySelector(selectors.nav);

        if (!fetchedNav) {
          throw new Error("Mobile menu markup was not found in the response");
        }

        const placeholder = nav.querySelector(selectors.placeholder);

        if (placeholder) {
          placeholder.remove();
        }

        // Keep the skeleton's close button. The header script binds to it
        // during init, so replacing it would break closing the drawer.
        Array.from(fetchedNav.children).forEach(function (child) {
          if (child.querySelector(selectors.closeButton)) {
            return;
          }

          nav.appendChild(child);
        });

        loadState = "loaded";
        bindTogglers();

        return true;
      })
      .catch(function (error) {
        loadState = "error";
        loadRequest = null;

        const placeholder = nav.querySelector(selectors.placeholder);

        if (placeholder) {
          placeholder.textContent = "";

          const message = document.createElement("p");
          message.className = "header-mobile__menu-error";
          message.textContent = "Menu unavailable. Please refresh the page.";
          placeholder.appendChild(message);
        }

        console.error(error);

        return false;
      });

    return loadRequest;
  }

  function bindTogglers() {
    nav.querySelectorAll(selectors.toggler).forEach(function (toggler) {
      if (boundTogglers.has(toggler)) {
        return;
      }

      boundTogglers.add(toggler);
      toggler.addEventListener("click", handleTogglerClick);
    });
  }

  function handleTogglerClick(event) {
    const toggler = event.currentTarget;
    const target = document.getElementById(toggler.dataset.target);

    if (!target) {
      return;
    }

    event.preventDefault();

    const isOpen = target.classList.contains(classes.active);

    if (isOpen) {
      closePanel(target);
      toggler.setAttribute("aria-expanded", "false");
      focusParent(target);
    } else {
      openPanel(target);
      toggler.setAttribute("aria-expanded", "true");

      nav.scrollTop = 0;
      focusFirst(target);
    }

    syncDrawerState();
  }

  function openPanel(panel) {
    window.clearTimeout(hideTimers.get(panel));

    panel.classList.remove(classes.hidden);

    window.requestAnimationFrame(function () {
      panel.classList.add(classes.active);
    });
  }

  function closePanel(panel) {
    panel.classList.remove(classes.active);

    window.clearTimeout(hideTimers.get(panel));
    hideTimers.set(
      panel,
      window.setTimeout(function () {
        if (!panel.classList.contains(classes.active)) {
          panel.classList.add(classes.hidden);
        }
      }, 700),
    );
  }

  function syncDrawerState() {
    const openPanels = nav.querySelectorAll(
      selectors.panel + "." + classes.active,
    );
    let hasNested = false;
    let hasDeepNested = false;

    openPanels.forEach(function (panel) {
      if (panel.dataset.menuType === "deep-nested") {
        hasDeepNested = true;
      } else if (panel.dataset.menuType === "nested") {
        hasNested = true;
      }
    });

    drawer.classList.toggle(classes.grandChildActive, hasDeepNested);
    drawer.classList.toggle(classes.childActive, hasNested);
  }

  function resetMenus() {
    nav.querySelectorAll(selectors.panel).forEach(function (panel) {
      window.clearTimeout(hideTimers.get(panel));
      panel.classList.remove(classes.active);
      panel.classList.add(classes.hidden);
    });

    nav.querySelectorAll(selectors.toggler).forEach(function (toggler) {
      toggler.setAttribute("aria-expanded", "false");
    });

    drawer.classList.remove(classes.childActive, classes.grandChildActive);
    releaseFocus();
  }

  function focusFirst(panel) {
    const utils = window.themeCore && window.themeCore.utils;
    const focusable = panel.querySelector(focusableSelector);

    if (utils && typeof utils.trapFocus === "function") {
      utils.trapFocus(
        panel,
        focusable ? { elementToFocus: focusable } : undefined,
      );
      return;
    }

    if (focusable) {
      window.setTimeout(function () {
        focusable.focus();
      }, 0);
    }
  }

  function releaseFocus() {
    const utils = window.themeCore && window.themeCore.utils;

    if (utils && typeof utils.removeTrapFocus === "function") {
      utils.removeTrapFocus();
    }
  }

  function focusParent(panel) {
    const parentPanel = panel.parentElement
      ? panel.parentElement.closest(selectors.panel)
      : null;

    if (parentPanel) {
      focusFirst(parentPanel);
      return;
    }

    focusFirst(nav);
  }

  function handleBurgerInteraction() {
    loadMenu();
  }

  function listenForDrawerClose() {
    if (!window.themeCore || !window.themeCore.EventBus) {
      return false;
    }

    window.themeCore.EventBus.listen(
      ["Toggle:headerToggleMenuDrawer:close"],
      resetMenus,
    );

    return true;
  }

  function init() {
    document.querySelectorAll(selectors.burger).forEach(function (burger) {
      burger.addEventListener("pointerdown", handleBurgerInteraction, {
        passive: true,
      });
      burger.addEventListener("focus", handleBurgerInteraction, {
        passive: true,
      });
      burger.addEventListener("click", handleBurgerInteraction);
    });

    const closeButton = nav.querySelector(selectors.closeButton);

    if (closeButton) {
      closeButton.addEventListener("click", resetMenus);
    }

    if (!listenForDrawerClose()) {
      document.addEventListener("theme:all:loaded", listenForDrawerClose, {
        once: true,
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
